"use client";

import React, { useEffect, useMemo, useRef } from "react";
import { Form, Input, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { createUseStyles } from "react-jss";
import IMask from "imask";

const UIInputMask = ({
  label,
  name,
  required,
  rules = [],
  defaultValue,
  disabled,
  placeholder,
  tooltip,
  mask,
  maskChar,
  onChange,
  compact = false,
  ...rest
}) => {
  const styles = useStyles();
  const form = Form.useFormInstance();
  const watchedValue = Form.useWatch(name, form);

  const inputRef = useRef(null);
  const maskRef = useRef(null);

  const normalizedMask = useMemo(() => {
    const m = mask ?? "00000-0000000-0";
    return String(m).replace(/\d/g, "0");
  }, [mask]);
  const expectedDigits = useMemo(
    () => (normalizedMask.match(/0/g) || []).length,
    [normalizedMask]
  );
  const formatExample = String(mask ?? "12345-6789123-4");

  const mergedRules = useMemo(() => {
    const list = [...(rules || [])];

    if (required && !list.some((r) => r?.required)) {
      list.unshift({
        required: true,
        message: `${typeof label === "string" ? label : "This field"} is required`,
      });
    }

    list.push({
      validator: async (_, value) => {
        if (!value) return;
        const digits = String(value).replace(/\D/g, "");
        if (digits.length !== expectedDigits) {
          throw new Error(`Please enter a valid format: ${formatExample}`);
        }
      },
    });

    return list;
  }, [rules, required, label, expectedDigits, formatExample]);

  useEffect(() => {
    const el = inputRef.current?.input;
    if (!el) return;

    if (maskRef.current) {
      maskRef.current.destroy();
      maskRef.current = null;
    }

    const m = IMask(el, {
      mask: normalizedMask,
      lazy: true,
      placeholderChar: maskChar ?? "_",
      overwrite: true,
    });
    const init =
      watchedValue != null ? String(watchedValue) :
        defaultValue != null ? String(defaultValue) : "";
    m.value = init;
    m.on("accept", () => {
      const val = m.value ?? "";
      form.setFieldValue(name, val);
      form.validateFields([name]).catch(() => { });
      onChange?.({ target: { id: name, name, value: val } });
    });

    maskRef.current = m;

    return () => {
      m.destroy();
      maskRef.current = null;
    };
  }, [normalizedMask, maskChar]);

  useEffect(() => {
    const m = maskRef.current;
    if (!m) return;
    const v = watchedValue == null ? "" : String(watchedValue);
    if (m.value !== v) m.value = v;
  }, [watchedValue]);

  return (
    <Form.Item
      className={styles.formItem}
      label={
        <span className={styles.labelRow}>
          <span>{label}</span>
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoCircleOutlined className={styles.infoIcon} />
            </Tooltip>
          )}
        </span>
      }
      name={name}
      rules={mergedRules}
      required={required}
      hasFeedback
      validateTrigger={["onChange", "onBlur"]}
      initialValue={defaultValue != null ? String(defaultValue) : ""}
    >
      <Input
        ref={inputRef}
        disabled={disabled}
        placeholder={placeholder || formatExample}
        className={compact ? styles.compactInput : styles.customSelect}
        {...rest}
      />
    </Form.Item>
  );
};

export default UIInputMask;

const useStyles = createUseStyles({
  formItem: {
    "& .ant-form-item-label": {
      display: "block",
      width: "100%",
    },
    "& .ant-form-item-label > label": {
      display: "flex",
      width: "100% !important",
    },
  },
  labelRow: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    justifyContent: "space-between",
    minWidth: 0,
    width: "100%",
  },
  infoIcon: {
    color: "#8C8C8C",
    cursor: "help",
    fontSize: 14,
  },
  compactInput: {
    width: "100%",
    height: "42px !important",
    padding: "8px 12px",
    borderRadius: 22,
    fontSize: 14,
    fontWeight: 500,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.04)",
  },
  customSelect: {
    width: "100%",
    height: "51px !important",
    fontSize: "16px",
    fontWeight: 500,
  },
});
